import './assets/base.css'
import './config/sentry'

import { GoogleOAuthProvider } from '@react-oauth/google'
import { QueryClientProvider } from '@tanstack/react-query'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, Navigate } from 'react-router'
import { RouterProvider } from 'react-router/dom'

import {
  appSignedInRoutePath,
  billingRoutePath,
  configurationsRoutePath,
  passwordResetRoutePath,
  profileRoutePath,
  registerRoutePath,
  resetPasswordConfirmationRoutePath,
  serverEditionsRoutePath,
  signInRoutePath,
} from './config/routePaths'
import ExpanDriveProApp from './Layout/AppMain'
import { DocumentTitle } from './Layout/DocumentTitle'
import BillingPage from './Pages/Billing'
import { DeploymentsIndex } from './Pages/Deployments'
import { DeploymentsEditDrive } from './Pages/Deployments/Drives/edit'
import {
  DeploymentsEdit,
  DeploymentsEditAppSettingsTab,
  DeploymentsEditDrivesTab,
  DeploymentsEditLicenseSettingsTab,
} from './Pages/Deployments/edit'
import { DeploymentsNew } from './Pages/Deployments/new'
import DevicesPage from './Pages/Devices'
import { ProfilePage } from './Pages/Profile'
import ServerEditionsPage from './Pages/ServerEditions'
import { SettingsAccountTab, SettingsDefaultsTab, SettingsPage } from './Pages/Settings'
import AppSignedIn from './Pages/UserPages/AppSignedIn'
import Login from './Pages/UserPages/Login'
import { UsersIndex } from './Pages/Users'
import { UsersEdit } from './Pages/Users/edit'
import { publicAuthLoader, signOutLoader } from './utils/authRouteLoaders'
import queryClient from './utils/queryClient'
import { loadSessionData } from './utils/session'

const toRoutePath = routePath => routePath.replace(/^\//, '')
const titledRoute = (routePath, element, title) => ({
  path: toRoutePath(routePath),
  element,
  handle: { title },
})

const redirectToConfigurations = <Navigate replace to={configurationsRoutePath} />

loadSessionData()

const publicAuthRoute = ({ path, title }) => ({
  ...titledRoute(path, <Login />, title),
  loader: publicAuthLoader,
})

const router = createBrowserRouter([{
  element: <DocumentTitle />,
  children: [
    {
      path: '/',
      element: redirectToConfigurations,
    },
    ...[
      { path: signInRoutePath, title: 'Sign In' },
      { path: registerRoutePath, title: 'Register' },
      { path: passwordResetRoutePath, title: 'Reset Password' },
      { path: resetPasswordConfirmationRoutePath, title: 'Choose a New Password' },
    ].map(publicAuthRoute),
    titledRoute(appSignedInRoutePath, <AppSignedIn />, 'Signing in'),
    {
      handle: { title: 'Signing Out' },
      path: toRoutePath('/users/sign_out'),
      loader: signOutLoader,
    },
    {
      element: <ExpanDriveProApp />,
      children: [
        titledRoute(profileRoutePath, <ProfilePage />, 'Profile'),
        titledRoute(configurationsRoutePath, <DeploymentsIndex />, 'Configurations'),
        titledRoute(`${configurationsRoutePath}/new`, <DeploymentsNew />, 'New Configuration'),
        titledRoute(`${configurationsRoutePath}/:id/drives/new`, <DeploymentsEditDrive isNew />, 'New Drive'),
        titledRoute(`${configurationsRoutePath}/:id/drives/:driveId`, <DeploymentsEditDrive />, 'Edit Drive'),
        {
          handle: { title: 'Configuration' },
          path: toRoutePath(`${configurationsRoutePath}/:id`),
          element: <DeploymentsEdit />,
          children: [
            { index: true, element: <DeploymentsEditDrivesTab /> },
            { path: 'app-settings', element: <DeploymentsEditAppSettingsTab />, handle: { title: 'Application Settings' } },
            { path: 'license-settings', element: <DeploymentsEditLicenseSettingsTab />, handle: { title: 'License Settings' } },
          ],
        },
        titledRoute('/devices', <DevicesPage />, 'Active Devices'),
        titledRoute(serverEditionsRoutePath, <ServerEditionsPage />, 'Server Editions'),
        titledRoute('/users', <UsersIndex />, 'Active Users'),
        titledRoute('/users/new', <UsersEdit isNew />, 'Add User'),
        titledRoute('/users/:id', <UsersEdit />, 'Edit User'),
        {
          handle: { title: 'Account Settings' },
          path: toRoutePath('/settings'),
          element: <SettingsPage />,
          children: [
            { index: true, element: <SettingsAccountTab /> },
            { path: 'defaults', element: <SettingsDefaultsTab />, handle: { title: 'Drive Defaults' } },
          ],
        },
        titledRoute(billingRoutePath, <BillingPage />, 'Billing'),
        { path: '*', element: redirectToConfigurations },
      ],
    },
  ],
}])

const routes = (
  <GoogleOAuthProvider clientId={process.env.GOOGLE_CLIENT_ID}>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </GoogleOAuthProvider>
)

document.addEventListener('DOMContentLoaded', () => {
  const rootElement = document.getElementById('root')

  if (!rootElement) {
    return
  }

  const root = createRoot(rootElement)
  root.render(routes)
})
